import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import Breadcrumb from '../Common/Breadcrumb';
import { AmbassadorTermsReview } from '../Agreements/CommercialTerms';
import { Actions as COMMERCIAL } from '../../store/actions/commercialActions';
import { toggleAlertMessage } from '../../store/action';

const money = (value, currency = 'GBP') =>
  value == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value));

const BANK_STATUS = {
  missing: { label: 'Not provided', variant: 'destructive' },
  pending_verification: { label: 'Awaiting verification', variant: 'secondary' },
  verified: { label: 'Verified', variant: 'default' },
};

const FIELD_LABELS = {
  accountHolderName: 'account holder name',
  legalName: 'legal name',
  address: 'address',
  sortCode: 'sort code (6 digits)',
  accountNumber: 'account number (8 digits)',
  iban: 'IBAN',
  bic: 'BIC',
};

const EMPTY_BANK = {
  accountHolderName: '',
  legalName: '',
  address: '',
  taxRegistrationNumber: '',
  sortCode: '',
  accountNumber: '',
  iban: '',
  bic: '',
};

const EMPTY_TAX = {
  legalName: '',
  address: '',
  country: 'GB',
  legalForm: 'sole_trader',
  actsInBusiness: true,
  businessOwnership: 'owner',
  taxResidenceCountry: 'GB',
  taxIdentifier: '',
  taxIdentifierPermittedNone: false,
  vatRegistered: false,
  vatNumber: '',
  vatEffectiveDate: '',
  performanceCountries: 'GB',
  localRestrictionDeclared: false,
};

const EMPTY_RECIPIENT = {
  accountHolder: '',
  legalName: '',
  country: 'GB',
  currency: 'GBP',
  sortCode: '',
  accountNumber: '',
  iban: '',
  bic: '',
};

const Field = ({ id, label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete='off'
    />
  </div>
);

const BankDetailsForm = ({ payoutCurrency, onSave }) => {
  const [form, setForm] = useState(EMPTY_BANK);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const gbp = payoutCurrency === 'GBP';

  const submit = async () => {
    setBusy(true);
    const payload = gbp
      ? { ...form, iban: undefined, bic: undefined }
      : { ...form, sortCode: undefined, accountNumber: undefined };
    const ok = await onSave(payload);
    setBusy(false);
    if (ok) setForm(EMPTY_BANK);
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
      <Field id='bank-holder' label='Account holder name' value={form.accountHolderName} onChange={set('accountHolderName')} />
      <Field id='bank-legal' label='Legal name' value={form.legalName} onChange={set('legalName')} />
      <div className='md:col-span-2'>
        <Field id='bank-address' label='Address' value={form.address} onChange={set('address')} />
      </div>
      <Field
        id='bank-tax'
        label='VAT / tax registration number (optional)'
        value={form.taxRegistrationNumber}
        onChange={set('taxRegistrationNumber')}
      />
      <div />
      {gbp ? (
        <>
          <Field id='bank-sort' label='Sort code' value={form.sortCode} onChange={set('sortCode')} placeholder='00-00-00' />
          <Field id='bank-acct' label='Account number' value={form.accountNumber} onChange={set('accountNumber')} />
        </>
      ) : (
        <>
          <Field id='bank-iban' label='IBAN' value={form.iban} onChange={set('iban')} />
          <Field id='bank-bic' label='BIC' value={form.bic} onChange={set('bic')} />
        </>
      )}
      <div className='md:col-span-2'>
        <Button onClick={submit} loading={busy}>
          Save bank details
        </Button>
      </div>
    </div>
  );
};

const TaxProfileForm = ({ onSave, onValidateVat }) => {
  const [form, setForm] = useState(EMPTY_TAX);
  const [busy, setBusy] = useState(false);
  const [vatBusy, setVatBusy] = useState(false);
  const [vatResult, setVatResult] = useState(null);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    setBusy(true);
    const payload = {
      ...form,
      performanceCountries: String(form.performanceCountries || '')
        .split(/[\s,]+/)
        .filter(Boolean),
      taxIdentifier: form.taxIdentifierPermittedNone && !form.taxIdentifier ? 'none' : form.taxIdentifier,
    };
    const ok = await onSave(payload);
    setBusy(false);
    if (ok) setForm(EMPTY_TAX);
  };

  const validateVat = async () => {
    if (!form.vatNumber) return;
    setVatBusy(true);
    const res = await onValidateVat(form.vatNumber);
    setVatBusy(false);
    setVatResult(res);
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
      <Field id='tax-legal' label='Legal name' value={form.legalName} onChange={set('legalName')} />
      <Field id='tax-form' label='Legal form' value={form.legalForm} onChange={set('legalForm')} placeholder='sole_trader / ltd' />
      <div className='md:col-span-2'>
        <Field id='tax-address' label='Address' value={form.address} onChange={set('address')} />
      </div>
      <Field id='tax-country' label='Country' value={form.country} onChange={set('country')} />
      <Field id='tax-residence' label='Tax residence country' value={form.taxResidenceCountry} onChange={set('taxResidenceCountry')} />
      <Field id='tax-ownership' label='Business ownership' value={form.businessOwnership} onChange={set('businessOwnership')} />
      <Field
        id='tax-perf'
        label='Performance countries (comma-separated)'
        value={form.performanceCountries}
        onChange={set('performanceCountries')}
        placeholder='GB, MT'
      />
      <div className='flex items-center gap-3'>
        <Switch checked={!!form.actsInBusiness} onCheckedChange={set('actsInBusiness')} />
        <Label>Acts in business capacity</Label>
      </div>
      <div className='flex items-center gap-3'>
        <Switch checked={!!form.localRestrictionDeclared} onCheckedChange={set('localRestrictionDeclared')} />
        <Label>Local restriction declared</Label>
      </div>
      <Field id='tax-id' label='Tax identifier' value={form.taxIdentifier} onChange={set('taxIdentifier')} />
      <div className='flex items-center gap-3'>
        <Switch checked={!!form.taxIdentifierPermittedNone} onCheckedChange={set('taxIdentifierPermittedNone')} />
        <Label>No tax identifier (permitted none)</Label>
      </div>
      <div className='flex items-center gap-3'>
        <Switch checked={!!form.vatRegistered} onCheckedChange={set('vatRegistered')} />
        <Label>VAT registered</Label>
      </div>
      <div />
      {form.vatRegistered && (
        <>
          <Field id='tax-vat' label='VAT number' value={form.vatNumber} onChange={set('vatNumber')} />
          <Field
            id='tax-vat-date'
            label='VAT effective date'
            type='date'
            value={form.vatEffectiveDate}
            onChange={set('vatEffectiveDate')}
          />
          <div className='md:col-span-2 flex flex-wrap items-center gap-3'>
            <Button type='button' variant='outline' size='sm' loading={vatBusy} onClick={validateVat}>
              Validate VAT
            </Button>
            {vatResult?.ok && (
              <span className='text-xs text-muted-foreground'>
                Result: {vatResult.data?.validation?.result || 'recorded'}
              </span>
            )}
            {vatResult && !vatResult.ok && <span className='text-xs text-destructive'>{vatResult.error}</span>}
          </div>
        </>
      )}
      <div className='md:col-span-2'>
        <Button onClick={submit} loading={busy}>
          Save tax profile
        </Button>
      </div>
    </div>
  );
};

const SelfBillingAcceptCard = ({ onAccept }) => {
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const submit = async () => {
    setBusy(true);
    const ok = await onAccept({
      billingLegalEntityId: 'cyf-uk-ltd',
      documentVersion: '1.0',
      trigger: 'initial',
    });
    setBusy(false);
    if (ok) setAccepted(true);
  };

  return (
    <Card className='bg-card text-card-foreground'>
      <CardHeader>
        <CardTitle className='text-base'>Self-billing agreement</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-sm text-muted-foreground'>
          Accept Compare Your Footprint issuing Self-Billed Invoices or Payment Statements on your behalf for CYF UK
          Ltd. You can re-accept after a material tax-status change.
        </p>
        {accepted ? (
          <Badge variant='default'>Accepted</Badge>
        ) : (
          <Button onClick={submit} loading={busy}>
            Accept self-billing terms
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const PayoutRecipientForm = ({ payoutCurrency, onSave }) => {
  const [form, setForm] = useState({ ...EMPTY_RECIPIENT, currency: payoutCurrency || 'GBP' });
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const gbp = (form.currency || payoutCurrency) === 'GBP';

  const submit = async () => {
    setBusy(true);
    const payload = {
      ...form,
      billingLegalEntityId: 'cyf-uk-ltd',
      sourceCurrency: form.currency || 'GBP',
    };
    const ok = await onSave(payload);
    setBusy(false);
    if (ok) setForm({ ...EMPTY_RECIPIENT, currency: payoutCurrency || 'GBP' });
  };

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
      <Field id='recv-holder' label='Account holder' value={form.accountHolder} onChange={set('accountHolder')} />
      <Field id='recv-legal' label='Legal name' value={form.legalName} onChange={set('legalName')} />
      <Field id='recv-country' label='Country' value={form.country} onChange={set('country')} />
      <Field id='recv-currency' label='Currency' value={form.currency} onChange={set('currency')} />
      {gbp ? (
        <>
          <Field id='recv-sort' label='Sort code' value={form.sortCode} onChange={set('sortCode')} placeholder='00-00-00' />
          <Field id='recv-acct' label='Account number' value={form.accountNumber} onChange={set('accountNumber')} />
        </>
      ) : (
        <>
          <Field id='recv-iban' label='IBAN' value={form.iban} onChange={set('iban')} />
          <Field id='recv-bic' label='BIC' value={form.bic} onChange={set('bic')} />
        </>
      )}
      <div className='md:col-span-2'>
        <Button onClick={submit} loading={busy}>
          Save payout recipient
        </Button>
      </div>
    </div>
  );
};

/**
 * The Ambassador's own view (R-6107 / J-150): terms, customers, tax profile, self-billing
 * acceptance, documents, bank details and Wise payout recipient (R-6106).
 */
const AmbassadorPage = ({
  getAmbassadorView,
  saveAmbassadorBankDetails,
  saveAmbassadorTaxProfile,
  validateAmbassadorVat,
  acceptSelfBilling,
  listSelfBillingDocuments,
  savePayoutRecipient,
  showAlert,
}) => {
  const [view, setView] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const [res, docsRes] = await Promise.all([getAmbassadorView(), listSelfBillingDocuments()]);
    setLoading(false);
    if (res.ok) {
      setView(res.data);
      setError(null);
    } else {
      setError(res.error === 'forbidden' ? 'This organisation has no accepted Ambassador Agreement.' : res.error);
    }
    if (docsRes.ok) setDocuments(docsRes.data?.documents || docsRes.data || []);
  }, [getAmbassadorView, listSelfBillingDocuments]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onSaveBank = async (details) => {
    const res = await saveAmbassadorBankDetails(details);
    if (!res.ok) {
      const field = FIELD_LABELS[res.field];
      showAlert(true, field ? `Check the ${field}.` : res.error, 'error', 6000);
      return false;
    }
    showAlert(true, 'Bank details saved. Payouts resume once we have verified them.', 'success', 5000);
    reload();
    return true;
  };

  const onSaveTax = async (profile) => {
    const res = await saveAmbassadorTaxProfile(profile);
    if (!res.ok) {
      showAlert(true, res.error || 'Could not save tax profile.', 'error', 6000);
      return false;
    }
    showAlert(true, 'Tax profile saved.', 'success', 4000);
    reload();
    return true;
  };

  const onAccept = async (body) => {
    const res = await acceptSelfBilling(body);
    if (!res.ok) {
      showAlert(true, res.error || 'Could not accept self-billing.', 'error', 6000);
      return false;
    }
    showAlert(true, 'Self-billing terms accepted.', 'success', 4000);
    reload();
    return true;
  };

  const onSaveRecipient = async (details) => {
    const res = await savePayoutRecipient(details);
    if (!res.ok) {
      showAlert(true, res.error || 'Could not save payout recipient.', 'error', 6000);
      return false;
    }
    showAlert(true, 'Payout recipient saved. Awaiting Platform Admin confirmation.', 'success', 5000);
    reload();
    return true;
  };

  if (loading) {
    return (
      <div className='p-6 flex items-center gap-2 text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading…
      </div>
    );
  }
  if (error) return <p className='p-6 text-sm text-destructive'>{error}</p>;

  const currency = view.payoutCurrency || 'GBP';
  const balances = view.balances || {};
  const bankStatus = BANK_STATUS[view.bankDetailsStatus] || BANK_STATUS.missing;

  return (
    <div className='p-6 space-y-4'>
      <Breadcrumb items={[{ label: 'Ambassador', link: '/ambassador' }]} />

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        {[
          ['Held', balances.held],
          ['Payable', balances.payable],
          ['In settlement', balances.in_settlement],
          ['Paid', balances.paid],
        ].map(([label, value]) => (
          <Card key={label} className='bg-card text-card-foreground'>
            <CardContent className='pt-4'>
              <p className='text-xs text-muted-foreground'>{label}</p>
              <p className='text-xl font-semibold'>{money(value, currency)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {view.minimumPortfolio != null && !view.minimumMet && (
        <p className='text-sm text-warning'>
          Shares are held until you have {view.minimumPortfolio} paying Referred Customers at a month end.
        </p>
      )}
      {view.endDate && <p className='text-sm text-muted-foreground'>This agreement ends on {view.endDate}.</p>}

      <AmbassadorTermsReview summary={view.terms} />

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Referred customers</CardTitle>
        </CardHeader>
        <CardContent>
          {(view.customers || []).length === 0 ? (
            <p className='text-sm text-muted-foreground'>No referred customers yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className='border-border'>
                  <TableHead>Customer</TableHead>
                  <TableHead>Linked from</TableHead>
                  <TableHead>Linked to</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.customers.map((c) => (
                  <TableRow key={`${c.name}-${c.linkedFrom}`} className='border-border'>
                    <TableCell className='font-medium'>{c.name}</TableCell>
                    <TableCell>{c.linkedFrom}</TableCell>
                    <TableCell>{c.linkedTo || '—'}</TableCell>
                    <TableCell>
                      {c.demo ? <Badge variant='outline'>Demo</Badge> : c.active ? 'Linked' : 'Ended'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Tax profile</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-muted-foreground mb-3'>
            Required before Self-Billed Invoices can be issued. VAT registration needs a live validation check.
          </p>
          <TaxProfileForm onSave={onSaveTax} onValidateVat={validateAmbassadorVat} />
        </CardContent>
      </Card>

      <SelfBillingAcceptCard onAccept={onAccept} />

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Self-billing documents</CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No Self-Billed Invoices or Payment Statements yet. Documents are issued from monthly settlements.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className='border-border'>
                  <TableHead>Number</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead className='text-right'>Gross</TableHead>
                  <TableHead>Payout</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((d) => (
                  <TableRow key={d.id} className='border-border'>
                    <TableCell className='font-medium'>{d.number || d.id}</TableCell>
                    <TableCell>{d.documentKind || d.kind || '—'}</TableCell>
                    <TableCell>{d.issueDate || d.issuedAt || '—'}</TableCell>
                    <TableCell className='text-right'>{money(d.gross, d.currency || currency)}</TableCell>
                    <TableCell>{d.payoutStatus || d.status || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Self-billing statements</CardTitle>
        </CardHeader>
        <CardContent>
          {(view.statements || []).length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No statements yet. Statements are issued on the first of each month once your payable share reaches the
              settlement minimum{view.settlementMinimum != null ? ` of ${money(view.settlementMinimum, currency)}` : ''}.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className='border-border'>
                  <TableHead>Number</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead className='text-right'>Net</TableHead>
                  <TableHead className='text-right'>VAT</TableHead>
                  <TableHead className='text-right'>Gross</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.statements.map((s) => (
                  <TableRow key={s.id} className='border-border'>
                    <TableCell className='font-medium'>{s.number}</TableCell>
                    <TableCell>{s.issueDate}</TableCell>
                    <TableCell className='text-right'>{money(s.net, s.currency)}</TableCell>
                    <TableCell className='text-right'>{money(s.vat, s.currency)}</TableCell>
                    <TableCell className='text-right'>{money(s.gross, s.currency)}</TableCell>
                    <TableCell>{s.status === 'paid' ? `Paid ${s.paidAt || ''}` : 'Due'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className='bg-card text-card-foreground'>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-base'>Bank details</CardTitle>
          <Badge variant={bankStatus.variant}>{bankStatus.label}</Badge>
        </CardHeader>
        <CardContent className='space-y-4'>
          {view.bankDetails && (
            <p className='text-sm text-muted-foreground'>
              {view.bankDetails.accountHolderName} · account ending {view.bankDetails.accountLast4}
            </p>
          )}
          <p className='text-xs text-muted-foreground'>
            Payouts are made in {currency}. Saving new details pauses payouts until we verify them.
          </p>
          <BankDetailsForm payoutCurrency={currency} onSave={onSaveBank} />
        </CardContent>
      </Card>

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Payout recipient (Wise)</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-xs text-muted-foreground'>
            Destination used for prepared Wise transfers. Status stays unverified until a Platform Admin confirms.
          </p>
          <PayoutRecipientForm payoutCurrency={currency} onSave={onSaveRecipient} />
        </CardContent>
      </Card>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getAmbassadorView: () => dispatch(COMMERCIAL.getAmbassadorView()),
  saveAmbassadorBankDetails: (details) => dispatch(COMMERCIAL.saveAmbassadorBankDetails(details)),
  saveAmbassadorTaxProfile: (profile) => dispatch(COMMERCIAL.saveAmbassadorTaxProfile(profile)),
  validateAmbassadorVat: (vatNumber) => dispatch(COMMERCIAL.validateAmbassadorVat(vatNumber)),
  acceptSelfBilling: (body) => dispatch(COMMERCIAL.acceptSelfBilling(body)),
  listSelfBillingDocuments: () => dispatch(COMMERCIAL.listSelfBillingDocuments()),
  savePayoutRecipient: (details) => dispatch(COMMERCIAL.savePayoutRecipient(details)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(null, mapDispatchToProps)(AmbassadorPage);
